import { CircularProgress } from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import DefaultText from "../../components/Accessibility/DefaultText";
import PageTitle from "../../components/Accessibility/PageTitle";
import Tabs from "../../components/Tabs";
import { ComunicadosBenef } from "../../interfaces/entities/ComunicadosBenef";
import { TypeNotificationEnum } from "../../interfaces/enum/TypeNotificationEnum";
import { getComunicadosBenef } from "../../services/ComunicadoService";
import NotificationCard from "./NotificationCard";
import "./styles.css";
import { NotificationEnum } from "../../interfaces/enum/NotificationEnum";

function Notifications() {
  const [type, setType] = useState<string>("");
  const [notifications, setNotifications] = useState<ComunicadosBenef[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const navigate = useNavigate();
  const { notificationType } = useParams();

  const getNotifications = async () => {
    if (type === TypeNotificationEnum.Notificacoes) {
      await getAllNotifications();
    } else if (type === TypeNotificationEnum.SaudeOnline) {
      await getSaudeOnlineNotifications();
    } else {
      setLoading(true);

      try {
        const result = await getComunicadosBenef(type);

        if (result !== null) {
          const tipoNotificacao =
            type === "pesquisa_satisf"
              ? NotificationEnum.PesquisaSatisfacao
              : NotificationEnum.Mensagem;

          const comunicados = result.map((notification) => ({
            ...notification,
            tipoNotificacao,
          }));

          setNotifications(comunicados);
        }
      } catch (error) {
        console.error("Erro ao obter comunicados:", error);
      } finally {
        setLoading(false);
      }
    }
  };

  const removeDuplicateNotifications = (notifications: ComunicadosBenef[]) => {
    const uniqueIds = new Set();
    return notifications.filter((notification) => {
      if (!uniqueIds.has(notification.idComunic)) {
        uniqueIds.add(notification.idComunic);
        return true;
      }
      return false;
    });
  };

  const sortNotificationsByDate = (notifications: ComunicadosBenef[]) => {
    return notifications.sort((a, b) => {
      return new Date(b.dtIncl).getTime() - new Date(a.dtIncl).getTime();
    });
  };

  const getAllNotifications = async () => {
    setLoading(true);

    try {
      const allNotifications = await getComunicadosBenef(
        TypeNotificationEnum.Todos
      );

      if (allNotifications) {
        const categorizedNotifications = allNotifications.map(
          (notification) => {
            let tipoNotificacao: NotificationEnum;

            switch (notification.tipoComunic.codigo) {
              case TypeNotificationEnum.Notificacoes:
                tipoNotificacao = NotificationEnum.Notificacao;
                break;
              case TypeNotificationEnum.Comunicados:
                tipoNotificacao = NotificationEnum.Mensagem;
                break;
              case TypeNotificationEnum.SaudeOnline:
                tipoNotificacao = NotificationEnum.SaudeOnline;
                break;
              case TypeNotificationEnum.PesquisaSatisfacao:
                tipoNotificacao = NotificationEnum.PesquisaSatisfacao;
                break;
              default:
                tipoNotificacao = NotificationEnum.Mensagem;
            }

            return {
              ...notification,
              tipoNotificacao,
            };
          }
        );

        const uniqueSortedNotifications = sortNotificationsByDate(
          removeDuplicateNotifications(categorizedNotifications)
        );

        setNotifications(uniqueSortedNotifications);
      }
    } finally {
      setLoading(false);
    }
  };

  const getSaudeOnlineNotifications = async () => {
    setLoading(true);

    try {
      const saudeOnlineNotifications = await getComunicadosBenef(
        TypeNotificationEnum.SaudeOnline
      );

      if (saudeOnlineNotifications) {
        const result = saudeOnlineNotifications.map((notification) => ({
          ...notification,
          tipoNotificacao: NotificationEnum.SaudeOnline,
        }));

        const uniqueSortedNotifications = sortNotificationsByDate(
          removeDuplicateNotifications(result)
        );

        setNotifications(uniqueSortedNotifications);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (type !== "") {
      getNotifications();
    }
  }, [type]);

  useEffect(() => {
    if (notificationType !== undefined) {
      setType(notificationType);
    }
  }, [notificationType]);

  const handleTab = (position: string) => {
    navigate(`/notificacoes/${position}`);
    setType(position);
  };

  return (
    <div className="notifications">
      <div style={{ marginBottom: "48px" }}>
        <PageTitle text="Notificações" />
      </div>

      <Tabs handleTab={handleTab} tab={type} />
      {loading && (
        <div className="loading-holder">
          <CircularProgress />
        </div>
      )}
      {!loading && notifications.length === 0 && (
        <div style={{ margin: "56px 0" }}>
          <DefaultText text="Nenhuma notificação encontrada." />
        </div>
      )}
      {!loading && notifications.length > 0 && (
        <div className="notifications-holder">
          {notifications.map(
            (comunicadosBenef: ComunicadosBenef, index: number) => {
              return (
                <div key={`notification-card-${index}`}>
                  <NotificationCard
                    comunicadosBenef={comunicadosBenef}
                    selectedTab={type}
                  />
                  <div
                    className="notification-divider"
                    key={`notification-divider-${index}`}
                  />
                </div>
              );
            }
          )}
        </div>
      )}
    </div>
  );
}

export default Notifications;
